import React, { useEffect, useState } from 'react';
import { Box, List, ListItem, Tooltip, Divider } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import PostCarouselItem from '../../../../../../DesignSystem/PostCarouselItem';
import usePostsQuery from '../../../../../../../Hooks/usePostsQuery';
import PromptStep from './PromptStep';
import ProgressBar from './ProgressBar';
import Analytics from '../../../../../../../Services/AnalyticsService';
import { PostPhaseStatuses } from '../../../../../../../Utils/Types';

function TabPostsCarousel({
  parent,
  status,
  stepTab,
  space,
  setPostsView,
  emptyState,
  defaultView,
}: any) {
  const [page, setPage] = useState(0);
  const [counter, setCounter] = useState(1);
  const [showPrompt, setShowPrompt] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const { id: parentId } = parent;
  const onGoingStep = stepTab.dates_status === PostPhaseStatuses.ONGOING;

  const { posts, isFetching, postsCount, paging, refetch } = usePostsQuery(
    `carousel-for-${parentId}`,
    {
      limit: 1,
      space,
      parentId,
      status,
      sort: 'i_voted_for',
      page,
      useQueryProps: {
        keepPreviousData: false,
        cacheTime: 0,
      },
    },
  );

  useEffect(() => {
    if (postsCount) {
      setProgress(postsCount - parent.meta.children_current_user_has_not_voted_for_count);
      setTotal(postsCount);
    }
  }, [postsCount, parent.meta.children_current_user_has_not_voted_for_count]);

  const fetchNextPost = () => {
    Analytics.track('Post', 'Vote Post', `Skip vote from Carousel`, {
      'Post ID': posts[0]?.id,
      Reaction: 'skip',
      Origin: 'Carousel',
    });

    if (paging.page === paging.total_pages) {
      setPage(0);
    } else {
      setPage(paging.page);
    }
  };

  // Display an intermediary screen after the 5th displayed item, and then every 10 items (after #15, #25, etc.).
  // If the list is over, we keep on looping trough the ideas, it does not changee
  const showNext = (action: any) => {
    if (!showPrompt && counter % 10 === 5) {
      // if we met the frequency of the prompt
      // we cache the action to be performed, so we can perform it after the prompt
      setShowPrompt(action);
    } else if (action === 'skip') {
      setCounter(counter + 1);
      setShowPrompt(false);
      fetchNextPost();
    } else if (action === 'refetch') {
      setCounter(counter + 1);
      setShowPrompt(false);
      refetch();
    }
  };

  return (
    <>
      {postsCount === 0 && !isFetching ? (
        emptyState
      ) : (
        <>
          <Box
            sx={{
              mx: 2,
              mb: 1,
              alignSelf: 'center',
            }}
          >
            <Tooltip
              arrow
              title={
                <>
                  <FormattedMessage id="POSTS.CAROUSEL.PROGRESS.TOOLTIP_1" values={{ progress }} />{' '}
                  <FormattedMessage id="POSTS.CAROUSEL.PROGRESS.TOOLTIP_2" values={{ total }} />
                </>
              }
            >
              <ProgressBar total={total} count={progress} onClickNext={() => showNext('skip')} />
            </Tooltip>
          </Box>

          <List disablePadding>
            {isFetching && !posts.length ? (
              <div style={{ minHeight: isFetching && !posts.length ? 420 : 0 }} />
            ) : (
              posts.map((post: any) => (
                <div key={post.id} style={{ minHeight: 420 }}>
                  <Divider component="li" />
                  <ListItem sx={{ paddingY: 0 }} disableGutters>
                    {showPrompt && onGoingStep && (
                      <PromptStep
                        progress={counter}
                        handleNext={() => showNext(showPrompt)}
                        setPostsView={setPostsView}
                        space={space}
                        parentId={parentId}
                        openToCitizenAndContributor={!post.secured_strategies_contribute.restricted}
                      />
                    )}
                    {!showPrompt && post && (
                      <PostCarouselItem
                        post={post}
                        stepTab={stepTab}
                        space={space}
                        onClickVote={() => showNext('refetch')}
                        defaultView={defaultView}
                      />
                    )}
                  </ListItem>
                </div>
              ))
            )}
          </List>
        </>
      )}
    </>
  );
}

export default TabPostsCarousel;
